import { trackAd, useAdImpression } from "@/lib/adTrack";
import { Link } from "react-router-dom";
import { differenceInCalendarDays, parseISO } from "date-fns";
import { Home, MapPin, Star, Trees } from "lucide-react";
import { useGet } from "@/lib/api";
import { EVENT_CAT, eventCredit, eventImage, fmt, fmtTime, weatherInfo } from "@/lib/meta";
import { PhotoCredit } from "@/components/PhotoCredit";

const countdown = (ev) => {
  const days = differenceInCalendarDays(parseISO(ev.start), new Date());
  if (days <= 0) return ev.end && differenceInCalendarDays(parseISO(ev.end), new Date()) > 0 ? "Läuft gerade" : "Heute";
  return days === 1 ? "Morgen" : `in ${days} Tagen`;
};

const DayWeather = ({ ev }) => {
  const { data } = useGet("event-forecast", "/forecast", { lat: ev.lat, lng: ev.lng, date: ev.start.slice(0, 10) }, { enabled: ev.lat != null, staleTime: 1800_000 });
  if (!data?.available) return null;
  const { label, Icon } = weatherInfo(data.day.weather_code);
  return (
    <span className="flex items-center gap-1.5" title={label} data-testid={`mosaic-weather-${ev.id}`}>
      <Icon size={16} strokeWidth={1.5} /> {Math.round(data.day.temperature_2m_max)}° · {data.day.precipitation_probability_max ?? 0}% Regen
    </span>
  );
};

const Badges = ({ ev, lead }) => (
  <div className={`absolute left-4 top-4 gap-1.5 ${lead || ev.sponsored ? "flex" : "hidden md:flex"}`}>
    {ev.sponsored && <span className="eyebrow bg-white px-2 py-1 text-foreground" data-testid={`mosaic-ad-${ev.id}`}>Anzeige</span>}
    {ev.highlight && <span className="eyebrow flex items-center gap-1 bg-[#C9A56A] px-2 py-1 text-white"><Star size={11} className="fill-white" /> Highlight</span>}
    {ev.setting && (
      <span className="eyebrow flex items-center gap-1 bg-black/45 px-2 py-1 text-white backdrop-blur-sm">
        {ev.setting === "indoor" ? <Home size={11} /> : <Trees size={11} />}{ev.setting === "indoor" ? "Drinnen" : "Draußen"}
      </span>
    )}
  </div>
);

const Tile = ({ ev, lead, className, i }) => {
  const ref = useAdImpression(ev);
  return (
  <div ref={ref} className={`rise flex flex-col ${className}`} style={{ animationDelay: `${i * 70}ms` }}>
  <Link to={`/veranstaltungen/${ev.id}`} data-testid={`mosaic-tile-${ev.id}`} onClick={ev.sponsored ? () => trackAd(ev.id, "click") : undefined}
    className={`no-photo group relative block flex-1 overflow-hidden ${lead ? "min-h-[400px] md:min-h-[476px]" : "min-h-[200px] md:min-h-[230px]"}`}>
    {eventImage(ev) && <img src={eventImage(ev, lead ? 1200 : 700)} alt={ev.title} loading="lazy" className="img-tint absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />}
    <div className={`absolute inset-0 bg-gradient-to-t ${lead ? "from-black/85 via-black/40" : "from-black/85 via-black/55"} to-transparent`} />
    <Badges ev={ev} lead={lead} />
    <span className={`absolute right-4 top-4 bg-terracotta px-2 py-1 font-sub text-[0.7rem] text-white ${lead ? "" : "hidden md:inline"}`} data-testid={`mosaic-countdown-${ev.id}`}>{countdown(ev)}</span>
    <div className={`absolute inset-x-0 bottom-0 text-white [text-shadow:0_1px_8px_rgba(0,0,0,0.6)] ${lead ? "p-6 md:p-8" : "p-4 md:p-5"}`}>
      <p className="eyebrow text-sand">{fmt(ev.start, "EEE d. MMM")} · {fmtTime(ev.start)} Uhr<span className={lead ? "" : "hidden md:inline"}> · {EVENT_CAT[ev.category]?.label}</span></p>
      <h3 className={`mt-2 font-display leading-tight decoration-1 underline-offset-4 group-hover:underline ${lead ? "line-clamp-3 text-3xl md:text-5xl" : "line-clamp-2 text-lg md:text-2xl"}`}>{ev.title}</h3>
      <div className={`mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-white/80 ${lead ? "text-sm" : "text-xs"}`}>
        <span className="flex items-center gap-1.5"><MapPin size={13} /> {ev.region}</span>
        {lead && <DayWeather ev={ev} />}
      </div>
    </div>
  </Link>
  <PhotoCredit credit={eventCredit(ev)} className="mt-1" testid={`mosaic-credit-${ev.id}`} />
  </div>
  );
};

const rest = (n) => ({ 1: "col-span-2 md:col-span-12", 2: "md:col-span-6", 3: "md:col-span-4" })[n];

export const Mosaic = ({ items, testid }) => {
  const [lead, ...others] = items;
  const side = others.slice(0, 2);
  const bottom = others.slice(2);
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-12 md:gap-4" data-testid={testid}>
      <Tile ev={lead} lead i={0} className={`col-span-2 row-span-2 ${side.length ? "md:col-span-7" : "md:col-span-12"}`} />
      {side.map((ev, i) => <Tile key={ev.id} ev={ev} i={i + 1} className={`${side.length === 1 ? "col-span-2 md:row-span-2" : ""} md:col-span-5`} />)}
      {bottom.map((ev, i) => <Tile key={ev.id} ev={ev} i={i + 3} className={`${bottom.length === 1 || (bottom.length === 3 && i === 2) ? "col-span-2" : ""} ${rest(bottom.length)}`} />)}
    </div>
  );
};
